import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { registerDay, sessionRegisterStates, settledSessionRuns } from "@/components/courses/session-register-facts";
import type { BoxState, ListBox, Read, RowAct, RowTask } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import type { RegisterSession, SessionRegister } from "@/lib/api/attendance";
import { classTitle } from "@/lib/class-name";
import { classTime } from "@/lib/class-plan-name";
import { formatInCourseZone, formatSessionWindow } from "@/lib/session-portal";

import { readingAfter, rowTaskOf, type SessionRowReport, type SessionTaskKind } from "./row-task";
import { ALL_SESSIONS_COPY } from "./session-copy";
import { SessionRowTaskHost } from "./session-row-task";
import type { PlacedPlan } from "./use-plan-placement-tasks";
import type { SessionChangeKind } from "./use-session-change-task";

const copy = ALL_SESSIONS_COPY;

/** The work a session's row opens: its Prepare or Close, a change to the session itself, or a cancelled session's plan to move. */
export type SessionRowJob = SessionTaskKind | SessionChangeKind | "plan";

type UndatedPlan = SessionRegister["undated_plans"][number];

/**
 * A row of All sessions: one session, a run of held sessions that need
 * nothing, folded as one (C07), or a learner's published plan on no session (C10).
 */
export type AllSessionsRow =
  | { kind: "session"; session: RegisterSession }
  | { kind: "run"; sessions: RegisterSession[] }
  | { kind: "plan"; plan: UndatedPlan };

/** A future session not yet marked can be cancelled with others (R6). */
export function cancellable(session: RegisterSession): boolean {
  return session.status === "scheduled" && session.clock_phase === "upcoming" && session.roll !== "recorded";
}

/** The job a session's row opens first, or none: a session with no work of its own exits to its page. */
function firstJob(session: RegisterSession): SessionRowJob | null {
  if (session.status === "cancelled") return session.clock_phase === "upcoming" ? "reinstate" : session.plan ? "plan" : null;
  if (session.status !== "scheduled") return null;
  return session.clock_phase === "upcoming" ? "prepare" : session.clock_phase === "status_pending" ? "close" : null;
}

/** The other jobs a session's row offers once open. */
function otherJobs(session: RegisterSession, job: SessionRowJob): SessionRowJob[] {
  // A cancelled session's plan can be put on a session that meets.
  const plan: SessionRowJob[] = session.plan ? ["plan"] : [];
  const jobs: SessionRowJob[] = session.status === "cancelled"
    ? (session.clock_phase === "upcoming" ? ["reinstate", "move", ...plan] : plan)
    : cancellable(session) ? ["prepare", "move", "cancel"] : session.clock_phase === "status_pending" && session.status === "scheduled" ? ["close"] : [];
  return jobs.filter((item) => item !== job);
}

const JOB_VERB: Record<SessionRowJob, string> = {
  prepare: copy.prepare,
  close: copy.close,
  move: copy.change.verb.move,
  cancel: copy.change.verb.cancel,
  reinstate: copy.change.verb.reinstate,
  plan: copy.movePlan,
};

/** What a learner reads of a plan before its session: its objectives and preparation (C10). */
function planWords(plan: UndatedPlan): ReactNode {
  const objectives = plan.learning_objectives.filter((objective) => objective.trim());
  const preparation = plan.preparation_guidance?.trim();
  if (!objectives.length && !preparation) return null;
  return (
    <div className="grid gap-3 text-ui">
      {objectives.length > 0 && (
        <ul aria-label={copy.objectives} className="grid list-disc gap-1 ps-5 text-foreground">
          {objectives.map((objective, index) => <li key={index}>{objective}</li>)}
        </ul>
      )}
      {preparation && (
        <div className="grid gap-1">
          <p className="font-medium text-foreground">{copy.preparation}</p>
          <p className="whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]">{preparation}</p>
        </div>
      )}
    </div>
  );
}

function stateOf(session: RegisterSession, teaching: boolean, currentYear: number): BoxState | null {
  const words = sessionRegisterStates(session, session.plan, teaching, currentYear);
  if (!words.length) return null;
  const waiting = session.roll === "not_recorded" || (session.status === "scheduled" && session.clock_phase === "status_pending");
  const cls: BoxState["class"] = session.status === "cancelled" ? "quiet"
    : waiting ? "waiting"
      : session.status === "held" && words.length === 1 ? "done"
        : session.plan && session.plan.state !== "published" ? "draft" : "quiet";
  return { class: cls, word: words.join(" · ") };
}

/**
 * C07 All sessions: every session of the course in date order (P4), each
 * row saying only what differs (K3). The teaching team's Prepare and Close
 * open in the row (CASE-019), as do Move, Cancel and Reinstate (O1); a
 * future session is selected beside its mark for Cancel sessions (R6).
 */
export function useAllSessionsBox({
  read, timezone, teaching, canAct, termOpen, sessionHref, selected, onToggle, changeTask, placeTask, onCommitted, openSessionId, openJob,
  initialQuery = "", onQuery, cancelledOnly, marks = false,
}: {
  read: Read<SessionRegister>;
  timezone: string;
  teaching: boolean;
  /** The teaching team, in an open term, with the course's editing rights. */
  canAct: boolean;
  termOpen: boolean;
  sessionHref: (sessionId: string) => string;
  selected: readonly string[];
  onToggle: (sessionId: string) => void;
  changeTask: (session: RegisterSession, kind: SessionChangeKind, rowKey: string) => RowTask;
  /** Move plan: the Task that puts a cancelled session's plan on a session that meets. */
  placeTask: (plan: PlacedPlan, rowKey: string) => RowTask;
  /** Reads All sessions again once a row's Prepare or Close has committed. */
  onCommitted: () => void;
  /** The session the page opened, such as the session page's Move session. */
  openSessionId: string | null;
  openJob: SessionRowJob | null;
  /** The find a returned-to Desk restores (#1393 contract 4); the find shows it. */
  initialQuery?: string;
  /** Each change of the find, for a page that keeps it to restore. */
  onQuery?: (query: string) => void;
  /** Calendar order's one "Cancelled only" filter beside the find. */
  cancelledOnly?: { value: boolean; onChange: (value: boolean) => void };
  /** Calendar order marks the current session, or the next one when none is under way. */
  marks?: boolean;
}): { box: ListBox<SessionRegister, AllSessionsRow>; hosts: ReactNode; onRowOpenChange: (box: string, key: string, open: boolean) => void; hidden: (value: SessionRegister) => number } {
  const [query, setQueryState] = useState(initialQuery);
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    onQuery?.(next);
  }, [onQuery]);
  const [jobs, setJobs] = useState<Readonly<Record<string, SessionRowJob>>>(() => (openSessionId && openJob ? { [openSessionId]: openJob } : {}));
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set(openSessionId ? [openSessionId] : []));
  const [reports, setReports] = useState<ReadonlyMap<string, SessionRowReport>>(() => new Map());
  // The plan a row's Move plan opened on stays in that row once moved, so its result says where it went (W2).
  const [movedPlans, setMovedPlans] = useState<Readonly<Record<string, NonNullable<RegisterSession["plan"]>>>>({});
  const onReport = useCallback((sessionId: string, kind: SessionTaskKind, report: SessionRowReport) => {
    setReports((map) => new Map(map).set(`${sessionId}:${kind}`, report));
  }, []);
  const onRowOpenChange = useCallback((box: string, key: string, open: boolean) => {
    if (box !== "all-sessions") return;
    if (open) setOpened((keys) => (keys.has(key) ? keys : new Set(keys).add(key)));
  }, []);

  const currentYear = new Date().getFullYear();
  const needle = query.trim().toLocaleLowerCase();
  const onlyCancelled = cancelledOnly?.value === true;
  const filtering = Boolean(needle) || onlyCancelled;
  const matches = (session: RegisterSession) => (!onlyCancelled || session.status === "cancelled") && (!needle
    || [classTitle(session, timezone), session.session_date, registerDay(session.session_date, currentYear)].join(" ").toLocaleLowerCase().includes(needle));
  const rowsOf = (value: SessionRegister): AllSessionsRow[] => {
    const shown = value.sessions.filter(matches).map((session) => ({ session, plan: session.plan }));
    // A find shows each match on its own row; otherwise held sessions that need nothing fold as one.
    const groups = filtering ? shown.map((row) => [row]) : settledSessionRuns(shown, teaching, openSessionId);
    return [
      ...groups.map((group): AllSessionsRow => (group.length < 2 ? { kind: "session", session: group[0].session } : { kind: "run", sessions: group.map((row) => row.session) })),
      // The teaching team's plans on no session are their own box (C08); a learner's are this List's last rows (C10).
      ...(teaching ? [] : value.undated_plans.filter(planMatches).map((plan): AllSessionsRow => ({ kind: "plan", plan }))),
    ];
  };
  const planMatches = (plan: UndatedPlan) => !onlyCancelled && (!needle || plan.topic.toLocaleLowerCase().includes(needle));
  const jobOf = (session: RegisterSession): SessionRowJob | null => (canAct ? jobs[session.id] ?? firstJob(session) : null);
  const name = (session: RegisterSession) => classTitle(session, timezone, { underDay: true });
  const when = (session: RegisterSession) => {
    const time = session.starts_at ? session.ends_at ? formatSessionWindow(session.starts_at, session.ends_at, timezone) : classTime(session.starts_at, timezone) : "Time not scheduled";
    return `${registerDay(session.session_date, currentYear)} · ${time}`;
  };
  const runName = (sessions: RegisterSession[]) => {
    const label = (session: RegisterSession) => String(session.session_no ?? registerDay(session.session_date, currentYear));
    return copy.heldRun(label(sessions[0]), label(sessions.at(-1)!));
  };

  const value = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  // The session under way is current; otherwise the first scheduled one ahead is next.
  const markedId = marks && value ? (value.sessions.find((session) => session.status !== "cancelled" && session.clock_phase === "scheduled_now")
    ?? value.sessions.find((session) => session.status === "scheduled" && session.clock_phase === "upcoming")) : undefined;
  const marked = (session: RegisterSession, state: BoxState | null): BoxState | null => {
    if (!markedId || session.id !== markedId.id) return state;
    const word = session.clock_phase === "scheduled_now" ? copy.current : copy.next;
    return { class: state?.class ?? "quiet", word: state ? `${word} · ${state.word}` : word };
  };
  // A row keeps the job it opened with, so a Close that holds the session still shows its result there (W2).
  useEffect(() => {
    if (!value || !canAct) return;
    const unpinned = value.sessions.flatMap((session) => {
      const job = opened.has(session.id) && !(session.id in jobs) ? firstJob(session) : null;
      return job ? [[session.id, job] as const] : [];
    });
    if (unpinned.length) setJobs((current) => ({ ...Object.fromEntries(unpinned), ...current }));
  }, [value, canAct, opened, jobs]);
  useEffect(() => {
    if (!value) return;
    const held = value.sessions.flatMap((session) => (jobs[session.id] === "plan" && session.plan && movedPlans[session.id]?.id !== session.plan.id ? [[session.id, session.plan] as const] : []));
    if (held.length) setMovedPlans((current) => ({ ...current, ...Object.fromEntries(held) }));
  }, [value, jobs, movedPlans]);
  // The Prepare or Close a row opened is read with it, and stays mounted so its drafts survive Hide (O7).
  const hosts = value && canAct ? value.sessions.flatMap((session) => {
    const job = jobOf(session);
    if (!opened.has(session.id) || (job !== "prepare" && job !== "close")) return [];
    return [<SessionRowTaskHost key={`${session.id}:${job}`} sessionId={session.id} kind={job} onReport={onReport} />];
  }) : null;

  const box: ListBox<SessionRegister, AllSessionsRow> = {
    kind: "list",
    id: "all-sessions",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read,
    fold: "never",
    state: (current) => ({ class: "quiet", word: current.sessions.length || teaching ? copy.count(current.sessions.length) : copy.plans(current.undated_plans.length) }),
    detail: () => (teaching && !termOpen ? copy.closedTerm : ""),
    // All sessions is always open, so a closed term says so in its open head (CASE-025).
    openDetail: () => (teaching && !termOpen ? copy.closedTerm : undefined),
    rows: rowsOf,
    row: {
      key: (row) => (row.kind === "session" ? row.session.id : row.kind === "run" ? `run:${row.sessions[0].id}` : `plan:${row.plan.id}`),
      title: (row) => (row.kind === "session" ? name(row.session) : row.kind === "run" ? runName(row.sessions) : row.plan.topic),
      facts: (row) => (row.kind === "session" ? when(row.session) : row.kind === "run" ? `${registerDay(row.sessions[0].session_date, currentYear)} to ${registerDay(row.sessions.at(-1)!.session_date, currentYear)}` : ""),
      state: (row) => (row.kind === "session" ? marked(row.session, stateOf(row.session, teaching, currentYear)) : row.kind === "run" ? { class: "done", word: copy.held } : null),
      group: (row) => (row.kind === "plan" ? copy.unplaced : undefined),
      act: (row): RowAct | undefined => {
        if (row.kind === "run") return { verb: copy.show, does: "open" };
        if (row.kind === "plan") return planWords(row.plan) ? { verb: copy.show, does: "open" } : undefined;
        const job = jobOf(row.session);
        return job ? { verb: JOB_VERB[job], does: "open" } : { verb: copy.open, does: "exit", to: sessionHref(row.session.id) };
      },
      startsOpen: (row) => row.kind === "session" && row.session.id === openSessionId,
      task: (row) => {
        if (row.kind !== "session") return undefined;
        const job = jobOf(row.session);
        if (!job) return undefined;
        if (job === "move" || job === "cancel" || job === "reinstate") return changeTask(row.session, job, row.session.id);
        if (job === "plan") {
          const plan = row.session.plan ?? movedPlans[row.session.id];
          const task = plan && placeTask({ id: plan.id, topic: copy.itsPlan, state: plan.state, version: plan.updated_at, from: row.session }, row.session.id);
          // Once moved, the row keeps only the result: the session it went to is named there.
          return task && task.result.state === "confirmed" ? { ...task, parts: [] } : task || undefined;
        }
        const report = reports.get(`${row.session.id}:${job}`);
        return report?.status === "ready" && report.box ? readingAfter(rowTaskOf(report.box, row.session.id), onCommitted) : undefined;
      },
      detail: (row) => {
        if (row.kind === "plan") return planWords(row.plan);
        if (row.kind === "run") {
          return (
            <ul className="grid gap-1 text-ui">
              {row.sessions.map((session) => <li key={session.id}><Link data-nav="" className="inline-flex min-h-[var(--control-height-compact)] items-center text-primary underline-offset-4 hover:underline" to={sessionHref(session.id)}>{name(session)}</Link> · {when(session)}</li>)}
            </ul>
          );
        }
        const session = row.session;
        const job = jobOf(session);
        const report = job === "prepare" || job === "close" ? reports.get(`${session.id}:${job}`) : undefined;
        const others = job ? otherJobs(session, job) : [];
        return (
          <div className="grid gap-2">
            {(job === "prepare" || job === "close") && (!report || report.status === "loading") && <p role="status" className="text-ui text-muted-foreground">{copy.reading(name(session))}</p>}
            {report?.status === "failed" && (
              <p className="flex flex-wrap items-center gap-x-3 text-ui text-foreground">
                {copy.readRowFailed(name(session))}
                <Button type="button" variant="subtle" size="sm" onClick={report.retry}>{copy.tryAgain}</Button>
              </p>
            )}
            {report?.status === "refused" && <p className="text-ui text-foreground">{copy.readRowRefused(name(session))}</p>}
            {report?.status === "ready" && report.stale && (
              <p className="flex flex-wrap items-center gap-x-3 text-ui text-foreground" data-stale="">
                {copy.readRowStale(name(session), formatInCourseZone(report.stale.readAt, timezone, { hour: "numeric", minute: "2-digit" }))}
                <Button type="button" variant="subtle" size="sm" onClick={report.stale.retry}>{copy.tryAgain}</Button>
              </p>
            )}
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {others.map((other) => (
                <Button key={other} type="button" variant="subtle" size="sm" onClick={() => setJobs((current) => ({ ...current, [session.id]: other }))}>{JOB_VERB[other]}</Button>
              ))}
              {/* Another session's page is page navigation in the app, never a reload (CASE-032). */}
              <Link data-nav="" className="inline-flex min-h-11 items-center text-ui text-primary underline-offset-4 hover:underline" to={sessionHref(session.id)}>{copy.open}</Link>
            </p>
          </div>
        );
      },
      controls: canAct ? (row) => {
        if (row.kind !== "session" || !cancellable(row.session)) return null;
        // A native box drawn as the kit's choice mark: the whole line is the target, and it carries no state mark (F2).
        return (
          <label className="bento-opt">
            <input type="checkbox" aria-label={copy.pick(name(row.session))} checked={selected.includes(row.session.id)} onChange={() => onToggle(row.session.id)} />
            <span aria-hidden className="bento-opt-box" />
            <span>{copy.select}</span>
          </label>
        );
      } : undefined,
    },
    find: value && value.sessions.length > 0 ? {
      placeholder: copy.find,
      onQueryChange: setQuery,
      ...(onQuery ? { query } : {}),
      ...(cancelledOnly ? { facets: [{ label: copy.which, value: onlyCancelled ? "cancelled" : "all", options: [{ value: "all", label: copy.allSessions }, { value: "cancelled", label: copy.cancelledOnly }], onChange: (next: string) => cancelledOnly.onChange(next === "cancelled") }] } : {}),
      hidden: value.sessions.filter((session) => !matches(session)).length + (teaching ? 0 : value.undated_plans.filter((plan) => !planMatches(plan)).length),
    } : undefined,
  };
  const hidden = (current: SessionRegister) => current.sessions.filter((session) => selected.includes(session.id) && !matches(session)).length;
  return { box, hosts, onRowOpenChange, hidden };
}
